import React from 'react';
import { Info, GraduationCap, PiggyBank, Zap, AlertTriangle, Bookmark, School, Map } from 'lucide-react';

/**
 * Компонент с информацией о накоплениях на образование и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о накоплениях на образование в России
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-blue-500" />
          Высшее образование в России: стоимость и перспективы
        </h3>
        <p>
          Высшее образование в России – важная инвестиция в будущее. С каждым годом стоимость обучения растет, опережая инфляцию на 2-3%, что делает финансовое планирование критически важным для обеспечения качественного образования детям.
        </p>
        <p>
          <strong>Диапазон стоимости обучения в 2024-2025 учебном году:</strong>
        </p>
        <ul>
          <li><strong>Государственные вузы:</strong> от 90 000 до 350 000 рублей в год (в зависимости от программы и региона)</li>
          <li><strong>Частные вузы:</strong> от 150 000 до 450 000 рублей в год</li>
          <li><strong>Престижные вузы (топ-10):</strong> от 300 000 до 700 000 рублей в год</li>
          <li><strong>Специализированные программы:</strong> от 250 000 до 550 000 рублей в год</li>
        </ul>
        <p>
          Помимо платы за обучение, необходимо учитывать расходы на проживание, питание, учебные материалы, транспорт и другие повседневные нужды. Для студентов, обучающихся не в родном городе, эти расходы могут составлять от 120 000 до 300 000 рублей в год.
        </p>

        <h3 className="flex items-center gap-2">
          <Map className="w-5 h-5 text-blue-500" />
          География высшего образования
        </h3>
        <p>
          Стоимость образования и сопутствующих расходов значительно различается в зависимости от региона:
        </p>
        <ul>
          <li><strong>Москва и Санкт-Петербург:</strong> самая высокая стоимость обучения и проживания, но и наиболее престижные вузы с лучшими перспективами трудоустройства</li>
          <li><strong>Крупные региональные центры</strong> (Казань, Новосибирск, Екатеринбург, Томск): баланс между качеством образования и стоимостью</li>
          <li><strong>Небольшие города:</strong> более доступные цены, но может быть ограничен выбор специальностей и перспектив</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Стратегии накоплений на образование
        </h3>
        <p>
          Накопление средств на образование требует долгосрочного планирования. Вот наиболее эффективные стратегии:
        </p>
        <ol>
          <li>
            <strong>Начинайте рано:</strong> Чем раньше вы начнете откладывать, тем большую выгоду получите от сложного процента. Начало накоплений при рождении ребенка может значительно снизить необходимую ежемесячную сумму.
          </li>
          <li>
            <strong>Регулярность вложений:</strong> Систематические ежемесячные взносы более эффективны, чем нерегулярные крупные вложения.
          </li>
          <li>
            <strong>Образовательные счета и программы:</strong> Используйте специальные финансовые продукты для накоплений на образование, которые предлагают банки и страховые компании.
          </li>
          <li>
            <strong>Диверсификация инвестиций:</strong> Распределяйте накопления между различными финансовыми инструментами для оптимального баланса доходности и риска.
          </li>
        </ol>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium flex items-center gap-2">
            <Bookmark className="w-4 h-4" />
            Финансовые инструменты для накоплений
          </h4>
          <ul className="text-green-600 dark:text-green-400 text-sm mb-0">
            <li><strong>Банковские вклады:</strong> низкий риск, но и относительно невысокая доходность (4-7% годовых)</li>
            <li><strong>Индивидуальные инвестиционные счета (ИИС):</strong> налоговые льготы при сроке от 3 лет</li>
            <li><strong>ПИФы и ETF:</strong> более высокая потенциальная доходность при среднем уровне риска</li>
            <li><strong>Накопительное страхование жизни:</strong> защита средств в случае неблагоприятных событий</li>
            <li><strong>Специальные образовательные программы:</strong> целевые продукты от финансовых организаций</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <School className="w-5 h-5 text-blue-500" />
          Государственная поддержка образования
        </h3>
        <p>
          Российское государство предоставляет различные возможности поддержки образования:
        </p>
        <ol>
          <li>
            <strong>Материнский (семейный) капитал:</strong> Может быть направлен на образование любого из детей. На 2023 год размер материнского капитала составляет:
            <ul>
              <li>На первого ребенка: 524 500 рублей</li>
              <li>На второго ребенка (если не получали на первого): 693 100 рублей</li>
              <li>Дополнительно на второго ребенка: 168 600 рублей</li>
            </ul>
          </li>
          <li>
            <strong>Налоговый вычет за обучение:</strong> 13% от расходов на образование, но не более 15 600 рублей в год (из расчета максимальной суммы к вычету 120 000 рублей). Доступен для собственного обучения и обучения детей до 24 лет на очной форме.
          </li>
          <li>
            <strong>Региональные программы поддержки:</strong> В различных регионах России действуют свои программы поддержки образования, стипендии и субсидии.
          </li>
          <li>
            <strong>Образовательные кредиты с государственной поддержкой:</strong> Выдаются под сниженную процентную ставку с возможностью отсрочки платежей на время обучения.
          </li>
        </ol>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Альтернативные пути снижения затрат на образование
        </h3>
        <ul>
          <li><strong>Бюджетные места:</strong> В России ежегодно выделяется около 525 000 бюджетных мест в вузах</li>
          <li><strong>Целевое обучение:</strong> Договор с будущим работодателем, который оплачивает образование</li>
          <li><strong>Стипендии и гранты:</strong> Академические, социальные и именные стипендии могут существенно снизить финансовую нагрузку</li>
          <li><strong>Образовательные онлайн-платформы:</strong> Альтернатива традиционному образованию с меньшими затратами</li>
          <li><strong>Профильные олимпиады и конкурсы:</strong> Победители и призеры имеют льготы при поступлении</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при планировании
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Учитывайте инфляцию в сфере образования, которая обычно выше общей</li>
            <li>Помните о дополнительных расходах (проживание, питание, учебные материалы)</li>
            <li>Заранее изучите возможность получения стипендий и грантов</li>
            <li>Рассматривайте государственные программы поддержки и налоговые льготы</li>
            <li>Имейте запасной план на случай недобора необходимой суммы к началу обучения</li>
          </ul>
        </div>

        <h3>Как вовлечь ребенка в финансовое планирование образования</h3>
        <p>
          Важно привлекать детей к обсуждению финансовых аспектов образования по мере их взросления:
        </p>
        <ul>
          <li>Объясняйте ценность образования и его стоимость соответственно возрасту</li>
          <li>Поощряйте вклад ребенка в накопления через подработку, достижения в учебе</li>
          <li>Обсуждайте различные образовательные пути, их преимущества и стоимость</li>
          <li>Вместе исследуйте возможности стипендий, грантов и финансовой помощи</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор накоплений поможет вам спланировать оптимальную стратегию обеспечения образования вашему ребенку с учетом всех важных факторов и возможностей государственной поддержки.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;